import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import { DefaultRouteTabs } from '@/components/_common';
import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import DailyMovementReportForm from '@/components/Reports/MovementReports/DailyMovementReportForm';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { SituationOptions } from '@/core/domain/reports/movements/workOrders/WorkOrderReport.types';
import { useDailyMovementReport } from '@/core/domain/reports/movements/dailyMovement/dailyMovement.hooks';
import {
  DailyMovementRequest,
  DailyMovementRequestForm,
  FormatType,
} from '@/core/domain/reports/movements/dailyMovement/dailyMovement.types';

function DailyMovementReportsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<DailyMovementRequestForm>();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const report = useReportGenerate();

  const generateReport = useDailyMovementReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const {
        situation,
        initialDate,
        finalDate,
        companiesIds,
        subGroupIds,
        customerIds,
        estimatorIds,
        ...values
      } = await form.validateFields();

      const obj: DailyMovementRequest = {
        ...values,
        //----------ReportSelectMonthFields----------
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
        companiesIds: companiesIds ? companiesIds.toString() : '',
        //------------------others-------------------
        subGroupIds: subGroupIds ? subGroupIds.toString() : '',
        customersIds: customerIds ? customerIds.toString() : '',
        estimatorIds: estimatorIds ? estimatorIds.toString() : '',
        format: format === 'CSV' ? FormatType.CSV : FormatType.PDF,
        //-----------------Situation-----------------
        open: situation.includes(SituationOptions.open),
        authorized: situation.includes(SituationOptions.authorized),
        closed: situation.includes(SituationOptions.closed),
        canceled: situation.includes(SituationOptions.canceled),
      };

      const result = await generateReport.mutateAsync(obj);
      if (callback) {
        callback(result);
      }
    } catch {}
    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'movimento_diário.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport(FormatType.PDF, (result) =>
      report.downloadFile({
        result,
        fileName: 'movimento_diário.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport(FormatType.CSV, (result) =>
      report.downloadFile({
        result,
        fileName: 'movimento_diário.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DailyMovementReportForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}

export default DailyMovementReportsPage;
